/**
 * The session and the ordering context, resolved before any surface renders
 * (ORDR-41; pattern: paio-ui-framework `packages/campaign/src/CampaignProvider.tsx`).
 *
 * TWO DOORS, DECIDED ONCE FROM THE ADDRESS AT MOUNT.
 *
 * The CUSTOMER door (`/order?site=<uuid>`) asks nothing of the server here: no
 * session read and no context read. A diner arriving by QR holds no grant and
 * the customer mode is ungated.
 *
 * The STAFF door reads the session with `useSession` from `@pirouz-ca/ui-hooks`
 * and, only once it is authenticated, `GET /api/v1/ordering/context` with the
 * session cookie. The door is fixed at mount on purpose: a signed-in manager
 * who taps "Order" in the bar keeps their nodes rather than being dropped to an
 * anonymous diner mid-shift.
 *
 * THREE CONTEXT ANSWERS, NEVER TWO (paio-ordering#24). 200 with nodes renders
 * what those capabilities allow. 200 with `nodes: []` renders the children with
 * no nodes, which the shell turns into "not set up to order". A 503, any other
 * non-200, a network failure or a body that is not a context renders an error
 * with a retry, and NEVER the notice: an outage must not tell a kitchen that it
 * holds nothing.
 *
 * THE HOST DOOR (ORDR-164). On a host that is not one of the app's own
 * (`STAFF_HOSTS`), an address naming no Site, no brand and no staff mode asks
 * `flow/?entry=BRAND_DOMAIN&host=` which brand the host is, and asks nothing
 * else: no session, no context. A brand opens the customer door at the chooser;
 * the 404 renders "not an ordering site" with no sign-in, because a diner on a
 * mistyped subdomain is not a member of staff who forgot to log in; a failure
 * offers a retry. The staff hosts never ask (`session/address.ts`).
 *
 * THE BUSINESS (ORDR-184). Above a resolved staff context sits the business
 * switcher: memberships plus the restaurants reached by grant. A switch posts
 * `/auth/me/active-firm` and then re-reads the context as a new attempt, so
 * the nodes below are always the new acting firm's.
 *
 * ONE IDENTITY AT A TIME (ORDR-204). The session is shared with every app in
 * the browser, and in production another app's tab changed its user under this
 * one. The context and the business list are each kept with the session user
 * they were read for, and `useSession` re-reads the session whenever the window
 * takes focus. A different user is a different key: what was read for the
 * previous one is never drawn again, and memberships, reach and the context are
 * read afresh. A refused switch re-reads the session too, since that refusal
 * is how the change first showed. A session that turns anonymous gets sign-in.
 * Beside the switcher the person's name and a sign-out (`AccountMenu.tsx`).
 *
 * ORDR-209: and a slot (`businessActions.ts`) the shell draws "Onboard
 * business" into, on the shell's own onboarding gate.
 *
 * COURTESY, NOT SECURITY. What is drawn follows these capabilities; the server
 * enforces the same set on every request.
 */

import { useCallback, useMemo, useEffect, useState, type ReactElement, type ReactNode } from "react";
import type { ActingNode } from "@pirouz-ca/ui-components";
import { useSession } from "@pirouz-ca/ui-hooks";
import { createApiClient, type ApiClient, type ApiResult } from "../api/client.js";
import { SESSION_ENDPOINT } from "../auth/config.js";
import { loginHref } from "../auth/returnPath.js";
import { STAFF_HOSTS } from "../config/env.js";
import { doorForAddress, siteFromSearch } from "./address.js";
import { AccountMenu } from "./AccountMenu.js";
import { BusinessActionsSlot } from "./businessActions.js";
import { BusinessSwitcher } from "./BusinessSwitcher.js";
import { readHostEntry, type HostEntry } from "./hostEntry.js";
import { parseContextNodes, type ContextNodeType } from "./contextNodes.js";
import { OrderingContext, type OrderingContextValue } from "./orderingContext.js";
import { useBusinesses } from "./useBusinesses.js";

export interface OrderingContextProviderProps {
  /** Injected for tests. Defaults to a client on `VITE_API_BASE_URL`. */
  readonly client?: ApiClient;
  /** Injected for tests. Defaults to this document's hostname. */
  readonly hostname?: string;
  /** Injected for tests. Defaults to `VITE_STAFF_HOSTS`. */
  readonly staffHosts?: readonly string[];
  /** Injected for tests. Defaults to a document load with `window.location.assign`. */
  readonly navigate?: (href: string) => void;
  readonly children: ReactNode;
}

const NO_NODES: readonly ActingNode[] = [];
const NO_SITES: Readonly<Record<string, string>> = {};
const NO_TYPES: Readonly<Record<string, ContextNodeType>> = {};

type ContextOutcome =
  | {
      readonly kind: "ready";
      readonly nodes: readonly ActingNode[];
      readonly siteIdByNode: Readonly<Record<string, string>>;
      readonly nodeTypeByNode: Readonly<Record<string, ContextNodeType>>;
    }
  | { readonly kind: "failed"; readonly correlationId: string | null };

function outcomeOf(result: ApiResult): ContextOutcome {
  if (!result.ok) {
    return { kind: "failed", correlationId: result.correlationId };
  }
  const parsed = parseContextNodes(result.body);
  return parsed === null
    ? { kind: "failed", correlationId: result.correlationId }
    : { kind: "ready", ...parsed };
}

function documentLoad(href: string): void {
  window.location.assign(href);
}

function currentAddress(): { pathname: string; search: string; hostname: string } {
  return typeof window === "undefined"
    ? { pathname: "/", search: "", hostname: "" }
    : { pathname: window.location.pathname, search: window.location.search, hostname: window.location.hostname };
}

export function OrderingContextProvider({
  client,
  hostname,
  staffHosts = STAFF_HOSTS,
  navigate = documentLoad,
  children,
}: OrderingContextProviderProps): ReactElement {
  const [address] = useState(currentAddress);
  const host = hostname ?? address.hostname;
  const door = doorForAddress(address.pathname, address.search, host, staffHosts);

  if (door === "customer") {
    return (
      <CustomerDoor siteId={siteFromSearch(address.search)} brandNodeId={null}>
        {children}
      </CustomerDoor>
    );
  }
  if (door === "host") {
    return (
      <HostDoor hostname={host} {...(client === undefined ? {} : { client })}>
        {children}
      </HostDoor>
    );
  }
  return (
    <StaffDoor
      returnTo={`${address.pathname}${address.search}`}
      navigate={navigate}
      {...(client === undefined ? {} : { client })}
    >
      {children}
    </StaffDoor>
  );
}

function CustomerDoor({
  siteId,
  brandNodeId,
  children,
}: {
  siteId: string | null;
  brandNodeId: string | null;
  children: ReactNode;
}): ReactElement {
  const value = useMemo<OrderingContextValue>(
    () => ({
      surface: "customer",
      siteId,
      brandNodeId,
      nodes: NO_NODES,
      siteIdByNode: NO_SITES,
      nodeTypeByNode: NO_TYPES,
      ownUserId: null,
      activeFirmId: null,
    }),
    [siteId, brandNodeId],
  );
  return <OrderingContext.Provider value={value}>{children}</OrderingContext.Provider>;
}

function HostDoor({
  client,
  hostname,
  children,
}: {
  client?: ApiClient;
  hostname: string;
  children: ReactNode;
}): ReactElement {
  const [api] = useState<ApiClient>(() => client ?? createApiClient());
  // The answer is kept WITH the attempt it answers, as in StaffDoor, so a late
  // reply to an earlier attempt never overwrites a newer one.
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<{ attempt: number; entry: HostEntry } | null>(null);

  useEffect(() => {
    let live = true;
    void readHostEntry(api, hostname).then((entry) => {
      if (live) {
        setAnswer({ attempt, entry });
      }
    });
    return () => {
      live = false;
    };
  }, [api, hostname, attempt]);

  const entry = answer !== null && answer.attempt === attempt ? answer.entry : null;
  if (entry === null) {
    return (
      <p role="status" data-testid="host-entry-loading" className="p-4 text-sm">
        Loading
      </p>
    );
  }
  if (entry.kind === "not-found") {
    return <NotAnOrderingSite />;
  }
  if (entry.kind === "failed") {
    return (
      <div role="alert" data-testid="host-entry-error" className="flex flex-col gap-2 p-4 text-sm">
        <p>We could not load this ordering site just now.</p>
        <p className="text-[var(--color-neutral-600)]">Reference: {entry.correlationId}</p>
        <button
          type="button"
          onClick={() => setAttempt((current) => current + 1)}
          className="min-h-11 self-start rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] px-4"
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <CustomerDoor siteId={null} brandNodeId={entry.brandNodeId}>
      {children}
    </CustomerDoor>
  );
}

/**
 * The host's 404 (ORDR-164). Neutral: no brand (there is none), no sign-in,
 * no link, and nothing that says whether the host was ever a brand's.
 */
function NotAnOrderingSite(): ReactElement {
  return (
    <main data-testid="not-an-ordering-site" className="flex min-h-dvh flex-col justify-center gap-3 p-5">
      <h1 className="text-2xl font-bold">This address isn&apos;t an ordering site</h1>
      <p className="text-base text-[var(--color-neutral-700)]">Check the address, or scan the code at your venue.</p>
    </main>
  );
}

function StaffDoor({
  client,
  returnTo,
  navigate,
  children,
}: {
  client?: ApiClient;
  returnTo: string;
  navigate: (href: string) => void;
  children: ReactNode;
}): ReactElement {
  const [api] = useState<ApiClient>(() => client ?? createApiClient());
  const session = useSession({ endpoint: SESSION_ENDPOINT });

  // Each attempt is numbered, and an outcome is kept WITH the attempt AND the
  // session user it answers (ORDR-204). "Loading" is then derived (no outcome
  // for this key yet) rather than set inside the effect, a late reply to an
  // earlier attempt can never overwrite a newer one, and nothing read for one
  // user is ever drawn for another.
  const [attempt, setAttempt] = useState(0);
  const [businessAttempt, setBusinessAttempt] = useState(0);
  const [answer, setAnswer] = useState<{ key: string; outcome: ContextOutcome } | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);

  const authenticated = session.status === "authenticated";
  const ownUserId = session.user?.id ?? null;
  const signedInName = session.user?.name || session.user?.email || "";
  const identity = `${attempt}:${ownUserId ?? ""}`;
  const businesses = useBusinesses(api, authenticated ? `${identity}:${businessAttempt}` : null);
  const activeFirmId = businesses?.kind === "ready" ? businesses.activeFirmId : null;

  useEffect(() => {
    if (!authenticated) {
      return;
    }
    // A per-run flag: under StrictMode the first run's reply is discarded and
    // the second run issues its own request, so nothing hangs on "loading".
    let live = true;
    void api.getJson("/ordering/context").then((result) => {
      if (live) {
        setAnswer({ key: identity, outcome: outcomeOf(result) });
      }
    });
    return () => {
      live = false;
    };
  }, [api, authenticated, identity]);

  const retry = useCallback((): void => {
    if (session.status === "error") {
      void session.refetch();
    }
    setAttempt((current) => current + 1);
  }, [session]);

  // After a change of business: the same path as a retry, minus the session.
  const reread = useCallback((): void => setAttempt((current) => current + 1), []);
  const retryBusinesses = useCallback((): void => setBusinessAttempt((current) => current + 1), []);
  // A refused switch is how another tab's change of account first showed in
  // production: ask the session who this is now.
  const { refetch } = session;
  const recheckSession = useCallback((): void => {
    void refetch();
  }, [refetch]);

  const outcome = answer !== null && answer.key === identity ? answer.outcome : null;
  const nodes = outcome?.kind === "ready" ? outcome.nodes : NO_NODES;
  const siteIdByNode = outcome?.kind === "ready" ? outcome.siteIdByNode : NO_SITES;
  const nodeTypeByNode = outcome?.kind === "ready" ? outcome.nodeTypeByNode : NO_TYPES;
  const value = useMemo<OrderingContextValue>(
    () => ({
      surface: "staff",
      siteId: null,
      brandNodeId: null,
      nodes,
      siteIdByNode,
      nodeTypeByNode,
      ownUserId,
      activeFirmId,
    }),
    [nodes, siteIdByNode, nodeTypeByNode, ownUserId, activeFirmId],
  );

  if (session.status === "error") {
    return <AccessUnavailable correlationId={null} onRetry={retry} />;
  }
  if (session.status === "anonymous") {
    return (
      <div role="alert" data-testid="sign-in-required" className="flex flex-col gap-2 p-4 text-sm">
        <p>Sign in to use the kitchen, staff and admin screens.</p>
        {/*
          A plain link and a document load: /login renders outside this provider
          (Root.tsx). `loginHref` carries this path only when it is a same-origin
          relative path (ORDR-63).
        */}
        <a
          href={loginHref(returnTo)}
          className="self-start rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] px-3 py-1"
        >
          Sign in
        </a>
      </div>
    );
  }
  if (session.status === "loading" || outcome === null) {
    return (
      <p role="status" data-testid="context-loading" className="p-4 text-sm">
        Checking your access
      </p>
    );
  }
  if (outcome.kind === "failed") {
    return <AccessUnavailable correlationId={outcome.correlationId} onRetry={retry} />;
  }
  return (
    <OrderingContext.Provider value={value}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div data-testid="business-bar" className="flex flex-wrap items-center gap-2">
          <BusinessSwitcher
            client={api}
            businesses={businesses}
            onRetry={retryBusinesses}
            onSwitched={reread}
            onRefused={recheckSession}
          />
          <div ref={setActionsSlot} className="flex items-center gap-2" />
        </div>
        <AccountMenu client={api} name={signedInName} navigate={navigate} />
      </div>
      <BusinessActionsSlot.Provider value={actionsSlot}>{children}</BusinessActionsSlot.Provider>
    </OrderingContext.Provider>
  );
}

function AccessUnavailable({
  correlationId,
  onRetry,
}: {
  correlationId: string | null;
  onRetry: () => void;
}): ReactElement {
  return (
    <div role="alert" data-testid="context-error" className="flex flex-col gap-2 p-4 text-sm">
      <p>We could not check your access just now. Nothing about your access has changed.</p>
      {correlationId !== null && (
        <p className="text-[var(--color-neutral-600)]">Reference: {correlationId}</p>
      )}
      <button
        type="button"
        onClick={onRetry}
        className="self-start rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] px-3 py-1"
      >
        Try again
      </button>
    </div>
  );
}
